import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Loader2, Package, QrCode, Users, X, XCircle } from "lucide-react";
import {
  ADDON_PENDING_DAYS,
  GUEST_CAPACITY_WARN_RATIO,
  canWithdrawAddon,
  formatTry,
  type GuestCapacity,
  type QrExtraPack,
  type WeddingAddonRow,
} from "@wedding/shared";

/**
 * "Misafir kotası" — organizatör ve admin düğün panelinin EN ÜSTÜNDEKİ ortak kart
 * (Admin ⇄ Organizer eşitliği, 2026-10-15). Her app KENDİ uç tabanını verir:
 * organizatör `/api/organizer/w/:slug`, admin `/api/admin/w/:slug`.
 *
 * - Toplam misafir / QR sınırı çubuğu; %90'da sarı, dolunca kırmızı + açıklama.
 * - "QR ile ekstra kayıt": varsayılan KAPALI; paket düğmesi → uyarı penceresi
 *   (fiyat + koşullar + "onaylıyorum") → paket HEMEN aktif, ödeme bekliyor.
 * - Kullanılmamış bekleyen paket geri çekilebilir; admin her açık paketi iptal
 *   eder ve ikram (ücretsiz) açabilir.
 * - Her aksiyon başarı/hata bildirimi verir (kendi toast'u).
 * - Üst şerit: düğünün PAKETİ (sipariş no ya da hesap planı) ve geçerli limitler +
 *   kullanım (fotoğraf/video, depolama, SMS) — organizatör paketini buradan görür.
 */

/** 10 GB / 750 MB — paket şeridi için. */
function formatBytes(n: number, lang: string): string {
  const nf = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 });
  return n >= 1024 ** 3 ? `${nf.format(n / 1024 ** 3)} GB` : `${nf.format(n / 1024 ** 2)} MB`;
}

type Toast = { msg: string; ok: boolean } | null;

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  const json = (await res.json().catch(() => null)) as { ok?: boolean; data?: T; error?: string } | null;
  if (!res.ok || !json?.ok) throw new Error(json?.error ?? `HTTP ${res.status}`);
  return json.data as T;
}

const ERROR_KEYS: Record<string, string> = {
  addons_unavailable: "guestCapacity.errors.unavailable",
  addons_disabled: "guestCapacity.errors.disabled",
  unknown_pack: "guestCapacity.errors.unknownPack",
  addon_in_use: "guestCapacity.errors.inUse",
  addon_duplicate: "guestCapacity.errors.duplicate",
  addon_paid: "guestCapacity.errors.paid",
  // Finansal denetim (2026-10-16)
  addon_pending_exists: "guestCapacity.errors.pendingExists",
  wedding_suspended: "guestCapacity.errors.suspended",
  reason_required: "guestCapacity.errors.reason",
};

export function GuestCapacityCard({ apiBase, role }: { apiBase: string; role: "organizer" | "admin" }) {
  const { t, i18n } = useTranslation();
  const [cap, setCap] = useState<GuestCapacity | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<QrExtraPack | null>(null);
  const [toast, setToast] = useState<Toast>(null);
  const isAdmin = role === "admin";
  const nf = new Intl.NumberFormat(i18n.language);
  const price = (n: number) => formatTry(n, i18n.language);

  const load = useCallback(async () => {
    try {
      setCap(await call<GuestCapacity>(`${apiBase}/capacity`));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [apiBase]);
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const errorText = (err: unknown) => {
    const code = err instanceof Error ? err.message : String(err);
    return ERROR_KEYS[code] ? t(ERROR_KEYS[code]) : t("guestCapacity.errors.generic", { error: code });
  };

  const open = async (pack: QrExtraPack, comp: boolean, reason: string) => {
    setBusy(`open-${pack.seats}`);
    try {
      const next = await call<GuestCapacity>(`${apiBase}/addons`, {
        method: "POST",
        // İkram (0 ₺) yalnız gerekçeyle — sunucu da denetler, deftere yazılır.
        body: JSON.stringify({ seats: pack.seats, accept: true, ...(comp ? { comp: true, reason } : {}) }),
      });
      setCap(next);
      setConfirm(null);
      setToast({ msg: t("guestCapacity.opened", { seats: pack.seats }), ok: true });
    } catch (err) {
      setToast({ msg: errorText(err), ok: false });
    } finally {
      setBusy(null);
    }
  };

  const withdraw = async (addon: WeddingAddonRow) => {
    const key = isAdmin ? "guestCapacity.cancelConfirmAdmin" : "guestCapacity.withdrawConfirm";
    if (!window.confirm(t(key, { seats: addon.seats }))) return;
    setBusy(`del-${addon.id}`);
    try {
      setCap(await call<GuestCapacity>(`${apiBase}/addons/${addon.id}`, { method: "DELETE" }));
      setToast({ msg: t("guestCapacity.withdrawn", { seats: addon.seats }), ok: true });
    } catch (err) {
      setToast({ msg: errorText(err), ok: false });
    } finally {
      setBusy(null);
    }
  };

  if (failed) {
    return (
      <section className="rounded-2xl border border-border bg-card px-5 py-4 text-sm text-muted-foreground" data-testid="guest-capacity">
        {t("guestCapacity.loadFailed")}{" "}
        <button type="button" onClick={() => void load()} className="underline underline-offset-2 cursor-pointer">
          {t("guestCapacity.retry")}
        </button>
      </section>
    );
  }
  if (!cap) {
    return <div className="h-36 rounded-2xl bg-muted animate-pulse" data-testid="guest-capacity-loading" />;
  }

  const ratio = cap.qrLimit > 0 ? cap.guests / cap.qrLimit : 1;
  const full = cap.guests >= cap.qrLimit;
  const warn = !full && ratio >= GUEST_CAPACITY_WARN_RATIO;
  const extraOn = cap.extraSeats > 0;
  const pendingTotal = cap.addons.filter((a) => a.status === "pending").reduce((s, a) => s + a.amount_try, 0);
  // Düğün başına aynı anda tek ödenmemiş paket; askıdaki düğüne paket açılmaz (sunucu da reddeder).
  const hasPending = cap.addons.some((a) => a.status === "pending");
  const suspended = cap.package?.source === "suspended";

  return (
    <section
      data-testid="guest-capacity"
      aria-labelledby="guest-capacity-title"
      className={[
        "rounded-2xl border bg-card overflow-hidden",
        full ? "border-red-500/50" : warn ? "border-amber-500/50" : "border-border",
      ].join(" ")}
    >
      {cap.package && (
        <div data-testid="guest-capacity-package" className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border bg-muted/20 px-5 py-3 text-sm">
          <p className="flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="text-muted-foreground">{t("guestCapacity.pkg.title")}</span>
            <span className="font-semibold text-foreground">{cap.package.label}</span>
            <span
              className={[
                "rounded-full px-2 py-0.5 text-[11px]",
                cap.package.source === "suspended" ? "bg-red-500/10 text-red-700 dark:text-red-400" : "bg-muted text-muted-foreground",
              ].join(" ")}
            >
              {cap.package.source === "order"
                ? t("guestCapacity.pkg.order", { number: cap.package.orderNumber })
                : cap.package.source === "account"
                  ? t("guestCapacity.pkg.account")
                  : cap.package.source === "free" || cap.package.source === "legacy" || cap.package.source === "suspended"
                    ? t(`guestCapacity.pkg.${cap.package.source}`)
                    : t("guestCapacity.pkg.trial")}
            </span>
          </p>
          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
            <div className="flex gap-1">
              <dt>{t("guestCapacity.pkg.media")}</dt>
              <dd className="tabular-nums text-foreground">
                {cap.usage ? `${nf.format(cap.usage.media)} / ` : ""}{nf.format(cap.package.limits.media)}
              </dd>
            </div>
            <div className="flex gap-1">
              <dt>{t("guestCapacity.pkg.storage")}</dt>
              <dd className="tabular-nums text-foreground">
                {cap.usage ? `${formatBytes(cap.usage.storageBytes, i18n.language)} / ` : ""}{formatBytes(cap.package.limits.storageBytes, i18n.language)}
              </dd>
            </div>
            <div className="flex gap-1">
              <dt>{t("guestCapacity.pkg.sms")}</dt>
              <dd className="tabular-nums text-foreground">
                {cap.usage ? `${nf.format(cap.usage.smsSegments)} / ` : ""}{nf.format(cap.package.limits.smsSegments)}
              </dd>
            </div>
          </dl>
        </div>
      )}
      <div className="px-5 py-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <h2 id="guest-capacity-title" className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Users className="h-4 w-4 text-primary" aria-hidden="true" />
            <span>{t("guestCapacity.title")}</span>
          </h2>
          <p className="text-2xl font-bold tabular-nums text-foreground" data-testid="guest-capacity-count">
            {nf.format(cap.guests)} <span className="text-base font-medium text-muted-foreground">/ {nf.format(cap.qrLimit)}</span>
          </p>
          <div
            className="h-2 w-full rounded-full bg-muted overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={cap.qrLimit}
            aria-valuenow={cap.guests}
            aria-label={t("guestCapacity.title")}
          >
            <div
              className={["h-full rounded-full transition-[width] duration-500", full ? "bg-red-500" : warn ? "bg-amber-500" : "bg-primary"].join(" ")}
              style={{ width: `${Math.min(ratio * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("guestCapacity.breakdown", { limit: nf.format(cap.limit), extra: nf.format(cap.extraSeats) })}
          </p>
          {(full || warn) && (
            <p
              role="status"
              data-testid="guest-capacity-alert"
              className={["flex items-start gap-1.5 text-sm", full ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-400"].join(" ")}
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{full ? t("guestCapacity.full") : t("guestCapacity.almost")}</span>
            </p>
          )}
        </div>

        <div className="md:w-[22rem] shrink-0 space-y-2 rounded-xl border border-border bg-muted/30 p-3">
          <p className="flex items-center justify-between gap-2 text-sm font-medium text-foreground">
            <span className="flex items-center gap-1.5">
              <QrCode className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>{t("guestCapacity.qrExtra")}</span>
            </span>
            <span
              data-testid="guest-capacity-extra-state"
              className={[
                "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                extraOn ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground",
              ].join(" ")}
            >
              {extraOn ? t("guestCapacity.on", { seats: nf.format(cap.extraSeats) }) : t("guestCapacity.off")}
            </span>
          </p>
          {!cap.addonsAvailable ? (
            <p className="text-xs text-muted-foreground">{isAdmin ? t("guestCapacity.migrationHint") : t("guestCapacity.errors.unavailable")}</p>
          ) : suspended ? (
            <p className="text-xs text-muted-foreground" data-testid="guest-capacity-suspended">{t("guestCapacity.errors.suspended")}</p>
          ) : !cap.packsEnabled && !isAdmin ? (
            <p className="text-xs text-muted-foreground">{t("guestCapacity.errors.disabled")}</p>
          ) : hasPending && !isAdmin ? (
            <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="guest-capacity-pending">{t("guestCapacity.pendingExists")}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {cap.packs.map((p) => (
                <button
                  key={p.seats}
                  type="button"
                  onClick={() => setConfirm(p)}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-background px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <span>{t("guestCapacity.packButton", { seats: p.seats, price: price(p.price) })}</span>
                </button>
              ))}
            </div>
          )}
          {cap.addons.length > 0 && (
            <ul className="space-y-1 pt-1" data-testid="guest-capacity-addons">
              {cap.addons.map((a) => {
                const removable = isAdmin || canWithdrawAddon(cap, a);
                return (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="tabular-nums">
                      +{nf.format(a.seats)} · {price(a.amount_try)} ·{" "}
                      {a.status === "paid" ? t("guestCapacity.statusPaid") : t("guestCapacity.statusPending")}
                    </span>
                    {removable && (
                      <button
                        type="button"
                        onClick={() => void withdraw(a)}
                        disabled={busy !== null}
                        className="underline underline-offset-2 hover:text-foreground cursor-pointer disabled:opacity-50"
                      >
                        {isAdmin ? t("guestCapacity.cancel") : t("guestCapacity.withdraw")}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {pendingTotal > 0 && (
            <p className="text-[11px] text-muted-foreground" data-testid="guest-capacity-due">
              {t("guestCapacity.due", { amount: price(pendingTotal) })}
            </p>
          )}
        </div>
      </div>

      {confirm && (
        <ConfirmDialog
          pack={confirm}
          cap={cap}
          isAdmin={isAdmin}
          busy={busy !== null}
          price={price}
          onCancel={() => setConfirm(null)}
          hasPending={hasPending}
          onConfirm={(comp, reason) => void open(confirm, comp, reason)}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={[
            "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
            toast.ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
          ].join(" ")}
        >
          {toast.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" /> : <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />}
          <span>{toast.msg}</span>
        </div>
      )}
    </section>
  );
}

function ConfirmDialog({
  pack,
  cap,
  isAdmin,
  busy,
  hasPending,
  price,
  onCancel,
  onConfirm,
}: {
  pack: QrExtraPack;
  cap: GuestCapacity;
  isAdmin: boolean;
  busy: boolean;
  hasPending: boolean;
  price: (n: number) => string;
  onCancel: () => void;
  onConfirm: (comp: boolean, reason: string) => void;
}) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const [comp, setComp] = useState(false);
  const [reason, setReason] = useState("");
  const reasonMissing = comp && reason.trim().length < 3;
  const titleId = useId();
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  return (
    <div className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => !busy && onCancel()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="guest-capacity-confirm"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <h3 id={titleId} className="text-base font-semibold text-foreground">
            {t("guestCapacity.confirmTitle", { seats: pack.seats })}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            aria-label={t("guestCapacity.close")}
            className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm text-foreground">
          <p className="text-2xl font-bold tabular-nums">{comp ? price(0) : price(pack.price)}</p>
          <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
            <li>{t("guestCapacity.confirmLimit", { from: cap.qrLimit, to: cap.qrLimit + pack.seats })}</li>
            <li>{t("guestCapacity.confirmQrOnly")}</li>
            <li>{t("guestCapacity.confirmBilling", { price: price(pack.price) })}</li>
            <li>{t("guestCapacity.confirmUnpaid", { days: ADDON_PENDING_DAYS })}</li>
            <li>{t("guestCapacity.confirmWithdraw")}</li>
          </ul>
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={comp} onChange={(e) => setComp(e.target.checked)} className="h-4 w-4 accent-primary cursor-pointer" />
              <span>{t("guestCapacity.comp")}</span>
            </label>
          )}
          {isAdmin && comp && (
            <label className="block space-y-1 text-sm">
              <span className="text-xs font-medium">{t("guestCapacity.compReason")}</span>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
                required
                autoComplete="off"
                aria-invalid={reasonMissing || undefined}
                placeholder={t("guestCapacity.compReasonPh")}
                data-testid="guest-capacity-comp-reason"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>
          )}
          {isAdmin && !comp && hasPending && (
            <p className="text-xs text-amber-700 dark:text-amber-400">{t("guestCapacity.pendingExists")}</p>
          )}
          <label className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm cursor-pointer select-none">
            <input
              ref={firstRef}
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-primary cursor-pointer"
            />
            <span>{t("guestCapacity.accept")}</span>
          </label>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted cursor-pointer"
          >
            {t("guestCapacity.close")}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(comp, reason.trim())}
            disabled={!accepted || busy || reasonMissing}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            <span>{t("guestCapacity.confirmButton", { seats: pack.seats })}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
